import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { RoleDashboard } from "@/components/dashboard/RoleDashboard";
import { PunchCard } from "@/components/dashboard/PunchCard";
import MobileHomePunchFAB from "@/components/dashboard/MobileHomePunchFAB";
import { SalesDashboardTiles } from "@/pages/sales/SalesDashboard";
import { PWAInstallBanner, AICostTile } from "@/components/PolishComponents";
import WhatsChangedSplash from "@/components/home/WhatsChangedSplash";
import BomOverrideTile from "@/components/dashboard/BomOverrideTile";
import MeetingActionsTile from "@/components/dashboard/MeetingActionsTile";
import ReceptionTodayCard from "@/components/dashboard/ReceptionTodayCard";
import PushNudge from "@/components/home/PushNudge";
import MissedTodayCard from "@/components/home/MissedTodayCard";
// D38 -- Onboarding + Offboarding checklist tasks assigned to me
import MyChecklistTasksWidget from "@/components/checklists/MyChecklistTasksWidget";
// D39 -- Performance self/manager review tasks
import MyPerformanceTasksWidget from "@/components/performance/MyPerformanceTasksWidget";
import InboxTile from "@/components/home/InboxTile";
import RelatedWorkTile from "@/components/home/RelatedWorkTile";
import { AnnouncementsHomeWidget } from "@/pages/AnnouncementsPage";
import CelebrationsWidget from "@/components/dashboard/CelebrationsWidget";
import { ShimmerDashboard } from "@/components/polish/Shimmer";
import { useAuth } from "@/store/auth";
import { useIsCEO } from "@/hooks/useIsCEO";
import { useMatrix, useMatrixAny } from "@/hooks/usePermission";

export default function Dashboard() {
  const { user } = useAuth();
  // D54-f Group F6 -- retired inline `user.role === "CEO" | "ADMIN" | ...`
  // checks in favor of the layered RBAC matrix. Same 3 flags = same
  // widgets; server routes still enforce their own keys.
  const isCEO = useIsCEO();
  const canPlatformAdmin = useMatrix("platform_admin_access");
  const isFde = useMatrix("front_desk_persona_ui");
  const canSalesTiles = useMatrixAny(
    "platform_admin_access", "sales_dashboard_scope_all",
    "sales_dashboard_scope_manager", "sales_dashboard_scope_sales_self",
    "sales_dashboard_scope_finance",
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/me");
        setData(data);
      } catch (_e) { /* dashboard fetch may 404 for new users */ }
      setLoading(false);
    })();
  }, []);

  // Punch card visible for all roles WITH an employee record (employee_code set).
  // CEO seed has an employee_code too so they see it.
  const hasEmployeeRecord = Boolean(user?.employee_code);

  if (loading) return <ShimmerDashboard />;
  return (
    <>
      <PushNudge />
      {/* D24 -- Company Announcements widget (pinned first). */}
      <div className="mb-3"><AnnouncementsHomeWidget /></div>
      {/* D24 -- Birthdays + work anniversaries. Auto-hides when empty. */}
      <div className="mb-3"><CelebrationsWidget /></div>
      <InboxTile />
      <RelatedWorkTile />
      <MissedTodayCard />
      <div className="mb-3"><MyChecklistTasksWidget /></div>
      <div className="mb-3"><MyPerformanceTasksWidget /></div>
      {hasEmployeeRecord && <PunchCard />}
      {/* D15 -- Mobile-only thumb-reach FAB. Hidden on lg+ via CSS. */}
      {hasEmployeeRecord && <MobileHomePunchFAB />}
      <PWAInstallBanner />
      <WhatsChangedSplash />
      <RoleDashboard data={data} />
      <div className="mt-4"><MeetingActionsTile /></div>
      {isCEO && <div className="mt-3"><AICostTile /></div>}
      {(canPlatformAdmin || isFde) && (
        <ReceptionTodayCard />
      )}
      {canPlatformAdmin && (
        <div className="mt-4"><BomOverrideTile /></div>
      )}
      {canSalesTiles && (
        <div className="mt-6"><SalesDashboardTiles /></div>
      )}
    </>
  );
}
